'use client'

import { useState, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import Editor from '@monaco-editor/react'

export default function ReplyForm({ threadId }: { threadId: string }) {
  const [content, setContent] = useState('')
  const [quotedUserId, setQuotedUserId] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  
  // Mounted state to track hydration
  const [isMounted, setIsMounted] = useState(false)
  
  const router = useRouter()
  const draftKey = `draft-reply-${threadId}`

  // Mark as mounted and load saved draft safely on the client
  useEffect(() => {
    setIsMounted(true)
    const savedDraft = localStorage.getItem(draftKey)
    if (savedDraft) {
      setContent(savedDraft)
    }
  }, [draftKey])

  useEffect(() => {
    const handleQuoteEvent = (e: any) => {
      const { content: quotedContent, authorName, authorId } = e.detail
      
      const doc = new DOMParser().parseFromString(quotedContent, 'text/html')
      let plainText = doc.body.textContent || ""
      
      if (authorName && plainText.toLowerCase().startsWith(authorName.toLowerCase())) {
        plainText = plainText.replace(new RegExp(`^${authorName}\\s*[:\\-]?\\s*`, 'i'), '')
      }
      
      setContent((prev: string) => {
        const displayName = authorName || 'Developer'
        const newContent = prev + (prev.length > 0 ? '\n\n' : '') + `> **${displayName}**\n> ${plainText.split('\n').join('\n> ')}\n\n`
        localStorage.setItem(draftKey, newContent)
        return newContent
      })
      
      if (authorId) {
        setQuotedUserId(authorId)
      }
    }

    window.addEventListener('add-quote', handleQuoteEvent)
    return () => window.removeEventListener('add-quote', handleQuoteEvent)
  }, [draftKey])

  // Track typing timeout reference
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null)

  const handleContentChange = (newContent: string) => {
    setContent(newContent)
    localStorage.setItem(draftKey, newContent)

    // Trigger typing indicator ON
    fetch(`/api/threads/${threadId}/typing`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ isTyping: true }),
    }).catch(() => {})

    if (typingTimeout) clearTimeout(typingTimeout)

    const timeout = setTimeout(() => {
      fetch(`/api/threads/${threadId}/typing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isTyping: false }),
      }).catch(() => {})
    }, 2000)

    setTypingTimeout(timeout)
  }

  // Monaco Editor Native Mention Completion Provider
  const handleEditorDidMount = (editor: any, monaco: any) => {
    monaco.languages.registerCompletionItemProvider('markdown', {
      triggerCharacters: ['@'],
      provideCompletionItems: async (model: any, position: any) => {
        const textUntilPosition = model.getValueInRange({
          startLineNumber: position.lineNumber,
          startColumn: 1,
          endLineNumber: position.lineNumber,
          endColumn: position.column,
        })
        
        const match = textUntilPosition.match(/@([a-zA-Z0-9_]*)$/)
        if (!match) return { suggestions: [] }

        const query = match[1]
        
        try {
          const res = await fetch(`/api/users/search?q=${encodeURIComponent(query)}`)
          const data = await res.json()
          
          if (!data.users) return { suggestions: [] }

          const suggestions = data.users.map((user: any) => ({
            label: `@${user.username || user.name}`,
            kind: monaco.languages.CompletionItemKind.User,
            insertText: `@${user.username || user.name} `,
            detail: user.name,
            documentation: `Mention ${user.name} (@${user.username || 'user'})`,
            range: {
              startLineNumber: position.lineNumber,
              startColumn: position.column - query.length,
              endLineNumber: position.lineNumber,
              endColumn: position.column,
            },
          }))

          return { suggestions }
        } catch (e) {
          return { suggestions: [] }
        }
      },
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      const res = await fetch('/api/posts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, threadId, quotedUserId })
      })

      const data = await res.json()

      if (!res.ok) throw new Error(data.error || 'Failed to post reply')

      // 🚀 Dispatch optimistic UI event with the new post payload
      window.dispatchEvent(
        new CustomEvent('post-created', { 
          detail: { post: data.post || data } 
        })
      )

      setContent('')
      setQuotedUserId(null)
      localStorage.removeItem(draftKey)
      
    } catch (err: any) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  if (!isMounted) {
    return (
      <div id="reply-form" className="bg-slate-900 border border-slate-800 rounded-md p-4 md:p-6 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-200 mb-4">Leave a Reply</h3>
        <div className="space-y-4">
          <div className="w-full h-[250px] bg-slate-950 border border-slate-800 rounded-md animate-pulse"></div>
          <div className="flex justify-end pt-2">
            <div className="w-32 h-10 bg-indigo-600/30 rounded-sm animate-pulse"></div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div id="reply-form" className="bg-slate-900 border border-slate-800 rounded-md p-4 md:p-6 shadow-sm flex flex-col">
      {error && (
        <div className="mb-4 p-3 bg-rose-950/40 border border-rose-900/50 text-rose-400 text-xs rounded-sm">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="w-full bg-slate-950 border border-slate-800 rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500 transition-all h-[250px]">
          <Editor
            height="100%"
            theme="vs-dark"
            language="markdown"
            value={content}
            onChange={(value) => handleContentChange(value || '')}
            onMount={handleEditorDidMount}
            loading={
              <div className="p-4 text-xs font-mono text-slate-500 flex items-center justify-center h-full gap-2">
                <svg className="animate-spin h-4 w-4 text-indigo-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                Booting IDE Engine...
              </div>
            }
            options={{
              minimap: { enabled: false },
              wordWrap: 'on',
              fontSize: 13,
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
              padding: { top: 16, bottom: 16 },
              scrollBeyondLastLine: false,
              smoothScrolling: true,
              cursorBlinking: "smooth",
              lineNumbers: "on",
              fixedOverflowWidgets: true,
              quickSuggestions: { other: true, comments: true, strings: true },
              suggestOnTriggerCharacters: true,
              scrollbar: {
                verticalScrollbarSize: 8,
                horizontalScrollbarSize: 8,
              }
            }}
          />
        </div>

        <div className="flex justify-between items-center pt-2">
          <span className="text-[10px] text-emerald-500/70 font-mono font-medium tracking-wider uppercase">
            {content.length > 0 ? '✓ Draft saved' : ''}
          </span>
          
          <button
            type="submit"
            disabled={loading || !content.trim()}
            className="bg-indigo-600 hover:bg-indigo-500 text-white font-mono text-xs px-6 py-2.5 rounded-sm transition-colors disabled:opacity-50 shadow-md border border-indigo-500/50"
          >
            {loading ? 'Executing...' : 'Post Reply'}
          </button>
        </div>
      </form>
    </div>
  )
}